import React from 'react';

export const millardFillmore = {
  presidentNumber: 13,
  president: 'Millard Fillmore',
  subheader: 'The Compromise President',
  years: '1850-1853',
  predecessor: 'Zachary Taylor',
  successor: 'Franklin Pierce',
  letter: {
    header: 'Letter to Franklin Pierce',
    content: `Dear General Pierce,
I write to you in the shadow of circumstances neither of us anticipated when we entered public service. The sudden loss of President Taylor thrust upon me burdens I never sought, yet I have endeavored to serve with the gravity these times demand.

You inherit a Union preserved, though at considerable cost. The Compromise of 1850 has, for now, stayed the hand of disunion, but I fear we have merely postponed rather than resolved our fundamental crisis. California enters as a free state, the territories of Utah and New Mexico may choose their own path regarding slavery, and we have strengthened the Fugitive Slave Law—each provision satisfying none completely, yet preventing immediate rupture.

I confess my deepest anguish over this last measure. While I believe it constitutionally necessary, I have witnessed the moral revulsion it creates among our Northern brethren. Every escaped slave returned to bondage weighs heavily upon my conscience, yet I could not permit the South to withdraw from our Union over this matter.

The forces of section grow stronger daily, and I fear that compromise—the very essence of our federal system—grows more difficult with each passing season. Senator Clay's great work may be our last such accommodation. You will need wisdom beyond my own to navigate these treacherous waters.

Beware the voices of extremism, North and South. The abolitionists care nothing for the Constitution they would see destroyed, while the fire-eaters would rend the Union our fathers died to create. The middle path grows narrow, but it remains the only road to preservation of our Republic.

May God grant you the strength I often lacked.

Your predecessor in trial, Millard Fillmore`
  },
  biographical: [
    'Born January 7, 1800, in Summerhill, New York',
    'Served partial term, July 9, 1850 – March 4, 1853',
    'Succeeded Zachary Taylor after his death',
    'Previously served as Vice President (1849-1850) and U.S. Representative',
    'Self-educated lawyer from poor background'
  ],
  presidencyFacts: [
    'Signed Compromise of 1850 to preserve Union',
    'Supported controversial Fugitive Slave Act',
    'Sent Commodore Perry to open Japan to trade',
    'Failed to win Whig nomination for full term',
    'Last Whig president'
  ],
  milestones: [
    'Became president after Taylor death (1850)',
    'Compromise of 1850 passed (1850)',
    'Fugitive Slave Act signed (1850)',
    'Perry expedition to Japan (1853)',
    'Lost Whig nomination to Scott (1852)'
  ],
  personalLife: [
    'Married Abigail Powers in 1826, then Caroline McIntosh in 1858',
    'Had two children with first wife',
    'Rose from poverty through self-education',
    'Known for his integrity and moderation',
    'Died on March 8, 1874, in Buffalo, New York'
  ],
  impactAndLegacy: [
    'Temporarily preserved Union through compromise',
    'Strengthened Fugitive Slave Act, angering North',
    'Helped destroy Whig Party through moderation',
    'Opened Japan to American trade',
    'Ranked among bottom tier of presidents'
  ],
  postPresidency: [
    'Retired to Buffalo, New York',
    'Ran for president on Know-Nothing ticket (1856)',
    'Supported Union during Civil War',
    'Died on March 8, 1874, at age 74',
    'Buried in Buffalo, New York'
  ],
  historicalAnalysis: `Millard Fillmore assumed the presidency under extraordinary circumstances, becoming the second vice president to succeed a president who died in office. Zachary Taylor's unexpected death from illness in July 1850 thrust Fillmore into the center of the most serious constitutional crisis since the founding of the Republic.

[SECTION] The Compromise of 1850

Fillmore's presidency was defined almost entirely by his handling of the sectional crisis over slavery's expansion into territories acquired from Mexico. Unlike his predecessor Taylor, who had opposed any expansion of slavery and threatened to veto compromise measures, Fillmore supported the package of bills that became known as the Compromise of 1850.

Working closely with Senator Henry Clay and later Stephen Douglas, Fillmore signed five separate bills: California's admission as a free state, the organization of New Mexico and Utah territories without restrictions on slavery (popular sovereignty), a stronger Fugitive Slave Act, the abolition of the slave trade in Washington D.C., and Texas border adjustments with federal assumption of Texas debts.

The Fugitive Slave Act proved particularly controversial and damaging to Fillmore's legacy. The law required federal marshals to assist in capturing runaway slaves and imposed penalties on anyone who aided escapees. Northern opposition was fierce, with many states passing personal liberty laws to circumvent federal requirements. The law effectively nationalized the slavery issue and radicalized Northern opinion against the South.

[SECTION] Foreign Policy and Trade

Despite domestic preoccupations, Fillmore achieved significant diplomatic successes. His administration dispatched Commodore Matthew Perry's expedition to Japan in 1853, which would lead to the opening of Japanese ports to American trade. Fillmore also supported Hungarian revolutionary Lajos Kossuth's visit to America, though he avoided formal intervention in European affairs.

The administration pursued aggressive commercial expansion, negotiating trade agreements with several South American nations and supporting the development of transcontinental communication links, including federal support for telegraph lines.

[SECTION] Political Isolation and Party Collapse

Fillmore's support for the Compromise of 1850 alienated him from both wings of the Whig Party. Northern Whigs opposed the Fugitive Slave Act, while Southern Whigs wanted stronger protections for slavery. His moderation pleased neither faction, and the Whig Party began its terminal decline during his presidency.

By 1852, Fillmore failed to win his party's nomination for a full term, losing to Mexican War hero Winfield Scott. The Whig Party's collapse left Fillmore politically homeless, and he would later accept the nomination of the anti-immigrant American (Know-Nothing) Party in 1856, finishing third in that election.

[SECTION] Assessment and Legacy

Historical evaluation of Fillmore has been mixed but generally critical. While some scholars credit him with temporarily preserving the Union through compromise, most argue that the Compromise of 1850 merely delayed the inevitable conflict over slavery while making it more bitter and irreconcilable.

The Fugitive Slave Act in particular has been seen as a moral failure that strengthened abolitionist sentiment and pushed the nation closer to war. Modern historians rank Fillmore among the bottom tier of presidents, typically placing him between 35th and 40th in presidential rankings.

Fillmore's tragedy was that of a decent man caught in impossible circumstances. His genuine desire to preserve the Union through moderation and compromise was admirable, but the fundamental contradiction between slavery and American ideals of liberty could not be resolved through political accommodation alone.`,
  sources: [
    '[LINK:https://archive.org/details/millardfillmoreb00rayb|Rayback, Robert J. Millard Fillmore: Biography of a President. Buffalo Historical Society, 1959.]',
    '[LINK:https://mcfarlandbooks.com/product/millard-fillmore/|Scarry, Robert J. Millard Fillmore. McFarland & Company, 2001.]',
    '[LINK:https://millercenter.org/president/fillmore/impact-and-legacy|Miller Center, University of Virginia. "Millard Fillmore: Impact and Legacy."]',
    '[LINK:https://global.oup.com/academic/product/the-origins-of-the-republican-party-1852-1856-9780195041007|Gienapp, William E. The Origins of the Republican Party, 1852-1856. Oxford University Press, 1987.]'
  ]
};

export default millardFillmore;